import React, { useState, useRef, useEffect } from "react";
import html2pdf from "html2pdf.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchSpeechThunk } from "../redux/fetchProjectsThunk";
import {
  lclUserAIIntegration,
  UserAIIntegration,
} from "../../../configs/WebService";
import userStore from "../../../zustand/userStore";
const AwarenessSpeechTab = ({ item, category_id = 1 }) => {
  const [selectedSpeechType, setSelectedSpeechType] = useState("");
  const [typedPrompt, setTypedPrompt] = useState("");
  const [displayedSpeech, setDisplayedSpeech] = useState("");
  const [isLoadingAiSpeech, setIsLoadingAiSpeech] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [language, setLanguage] = useState("English");

  const speechRef = useRef();
  const dispatch = useDispatch();

  const categoryKey = category_id === 1 ? "awareness" : "celebration";

  const userData = userStore((state) => state.user);
  const user_id = userData.admin_id;
  //Setting up user ai credentials
  //Fetch data here to put
  const models = userStore((state) => state.models);
  const selectedModelId = userStore((state) => state.selectedModelId);
  const setSelectedModelId = userStore((state) => state.setSelectedModelId);
  const fetchUserModels = userStore((state) => state.fetchUserModels);
  useEffect(() => {
    fetchUserModels(user_id);
  }, [fetchUserModels]);
  const handleSelectModel = (modelId) => {
    setSelectedModelId(modelId);
  };

  const {
    speeches = {
      staticSpeeches: { shortSpeech: [], longSpeech: [] },
      aiPrompts: [],
    },
    loading: loadingSpeech,
    error: fetchError,
  } = useSelector((state) => state.projects.eventDetails[categoryKey]);

  const staticSpeeches = speeches.staticSpeeches || {
    shortSpeech: [],
    longSpeech: [],
  };
  const aiPrompts = speeches.aiPrompts || [];

  useEffect(() => {
    if (item?.awareness_id) {
      dispatch(
        fetchSpeechThunk({ category_id, event_id: item.awareness_id, language })
      );
    }
  }, [dispatch, category_id, item?.awareness_id, language]);

  useEffect(() => {
    // Stop polling if we already have some speech data
    if (
      (staticSpeeches.shortSpeech && staticSpeeches.shortSpeech.length > 0) ||
      (staticSpeeches.longSpeech && staticSpeeches.longSpeech.length > 0) ||
      (aiPrompts && aiPrompts.length > 0)
    ) {
      return;
    }

    const intervalId = setInterval(() => {
      if (item?.awareness_id) {
        dispatch(
          fetchSpeechThunk({
            category_id,
            event_id: item.awareness_id,
            language,
          })
        );
      }
    }, 5000); // Fetch every 5 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [
    dispatch,
    category_id,
    item?.awareness_id,
    staticSpeeches,
    aiPrompts,
    language,
  ]);

  useEffect(() => {
    if (
      selectedSpeechType === "shortSpeech" &&
      staticSpeeches?.shortSpeech?.length > 0
    ) {
      const combinedSpeech = staticSpeeches.shortSpeech
        .map(
          (speech, index) =>
            `--- <strong>Speech ${index + 1}</strong>: <strong>${
              speech.title
            }</strong>---\n\n${speech.content}`
        )
        .join("\n\n\n");
      setDisplayedSpeech(combinedSpeech);
    } else if (
      selectedSpeechType === "longSpeech" &&
      staticSpeeches?.longSpeech?.length > 0
    ) {
      const combinedSpeech = staticSpeeches.longSpeech
        .map(
          (speech, index) =>
            `--- <strong>Speech ${index + 1}</strong>: <strong>${
              speech.title
            }</strong>---\n\n${speech.content}`
        )
        .join("\n\n\n");
      setDisplayedSpeech(combinedSpeech);
    } else if (selectedSpeechType === "aiGenerated") {
      setDisplayedSpeech("");
    } else {
      setDisplayedSpeech("");
    }
  }, [selectedSpeechType, staticSpeeches]);

  const handleSpeechTypeChange = (e) => {
    setSelectedSpeechType(e.target.value);
    setTypedPrompt("");
    setAiError(null);
  };

  const handleGenerateAiSpeech = async () => {
    if (!typedPrompt) {
      setAiError("Please enter a prompt.");
      return;
    }

    setIsLoadingAiSpeech(true);
    setAiError(null);
    setDisplayedSpeech("");

    const question = `Generate a speech ${typedPrompt}`;

    try {
      const response = await fetch(`${UserAIIntegration}/askAi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id,
          ai_model_id: selectedModelId,
          question,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to get AI response.");
      }

      const answer = result?.answer || "No response from AI.";
      setDisplayedSpeech(answer);
    } catch (err) {
      setAiError("Error: " + err.message);
      setDisplayedSpeech("Unable to generate response.");
    } finally {
      setIsLoadingAiSpeech(false);
    }
  };

  const handleCopy = () => {
    if (displayedSpeech) {
      navigator.clipboard.writeText(displayedSpeech);
      alert("Content copied to clipboard!");
    }
  };

  const handleDownloadPdf = () => {
    if (speechRef.current && displayedSpeech) {
      html2pdf().from(speechRef.current).save("speech-document.pdf");
    }
  };

  const handlePrint = () => {
    if (speechRef.current && displayedSpeech) {
      const printWindow = window.open("", "", "width=800,height=600");
      printWindow.document.write("<html><head><title>Print Document</title>");
      printWindow.document.write(
        "<style>body { font-family: sans-serif; line-height: 1.6; margin: 20px; }</style>"
      );
      printWindow.document.write("</head><body>");
      printWindow.document.write(
        "<pre>" + speechRef.current.innerText + "</pre>"
      );
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const applyMultiColumnPrompts = aiPrompts.length > 10; // Changed from 20 to 10 for demonstration as per updated request

  return (
    <div className="card card-body">
      <div className="d-flex justify-content-end mb-3">
        <div className="d-flex gap-2 w-100 justify-content-between">
          <div>
            <h3 className="text-18 my-0">Speech for {item.name || item.details}</h3>
            <p className="mb-0">
              Select a speech type or generate one using AI.
            </p>
          </div>
          <div>
          <div className="d-flex gap-2 ">
            <div className="input-group mb-0 me-2" style={{ width: "150px" }}>
              <select
                className="form-select mb-0"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
              </select>
            </div>
            <button
              className="btn btn-primary px-2 py-1 me-2"
              onClick={handleCopy}
              disabled={!displayedSpeech}
            >
              Copy
            </button>
            <button
              className="btn btn-primary me-2 px-2 py-1"
              onClick={handlePrint}
              disabled={!displayedSpeech}
            >
              Print
            </button>
            <button
              className="btn btn-primary px-2 py-1"
              onClick={handleDownloadPdf}
              disabled={!displayedSpeech}
            >
              Download PDF
            </button>
          </div>
          </div>

        </div>
        {
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {Array.isArray(models) &&
            models.length > 1 &&
            selectedSpeechType === "aiGenerated" ? (
              <select
                className="form-select"
                value={selectedModelId || ""}
                onChange={(e) => handleSelectModel(Number(e.target.value))}
              >
                <option value="" disabled>
                  Select a model
                </option>
                {models.map((model) => (
                  <option key={model.model_id} value={model.model_id}>
                    {model.name} — Verified: {model.verified ? "Yes" : "No"}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        }
      </div>

      <div className="mb-3">
        <label htmlFor="speechTypeSelect" className="form-label">
          Choose Speech Type:
        </label>
        <select
          id="speechTypeSelect"
          className="form-select"
          value={selectedSpeechType}
          onChange={handleSpeechTypeChange}
        >
          <option value="">-- Select --</option>
          <option value="shortSpeech">Short Speech</option>
          <option value="longSpeech">Long Speech</option>
          <option value="aiGenerated">Generate using AI</option>
        </select>
      </div>

      {selectedSpeechType === "aiGenerated" && (
        <div className="mb-3">
          <label htmlFor="typedPrompt" className="form-label">
            Type your own prompt:
          </label>
          <input
            type="text"
            id="typedPrompt"
            className="form-control"
            value={typedPrompt}
            onChange={(e) => setTypedPrompt(e.target.value)}
            placeholder="e.g., Write a speech on World Environment Day"
            disabled={isLoadingAiSpeech}
          />
          <button
            className="btn btn-primary mt-2"
            onClick={handleGenerateAiSpeech}
            disabled={!typedPrompt || isLoadingAiSpeech}
          >
            {isLoadingAiSpeech ? "Generating..." : "Generate Speech"}
          </button>
          {aiError && <div className="alert alert-danger mt-2">{aiError}</div>}

          {aiPrompts.length > 0 && (
            <div className="mt-3">
              <h6>Predefined Prompts:</h6>
              {/* Outer scrollable container for prompts */}
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  padding: "10px",
                }}
              >
                {/* Inner content container with conditional multi-column for prompts */}
                <ul
                  className="list-group"
                  style={
                    applyMultiColumnPrompts
                      ? {
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: "10px",
                        }
                      : {}
                  }
                >
                  {aiPrompts.map((prompt, index) => (
                    <li
                      key={index}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span dangerouslySetInnerHTML={{ __html: prompt }} />
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        title="Copy Prompt"
                        onClick={() =>
                          navigator.clipboard.writeText(stripHtmlTags(prompt))
                        }
                      >
                        📋
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedSpeechType && displayedSpeech && (
        <div className="mt-4">
          <h4>Speech Content:</h4>
          <div
            ref={speechRef}
            style={{
              backgroundColor: "#f8f9fa",
              padding: "1rem",
              border: "1px solid #ddd",
              minHeight: "100px",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {loadingSpeech && <p>Loading speeches...</p>}
            {fetchError && (
              <p className="text-danger">
                Error fetching speeches: {fetchError.message || fetchError}
              </p>
            )}
            {!loadingSpeech && !fetchError && (
              <div dangerouslySetInnerHTML={{ __html: displayedSpeech }} />
            )}
          </div>
        </div>
      )}

      {!selectedSpeechType && (
        <div className="alert alert-info mt-3">
          Please select a speech type from the dropdown above to begin.
        </div>
      )}
    </div>
  );
};

export default AwarenessSpeechTab;

const stripHtmlTags = (htmlString) => {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};
